// frontend/src/components/announcements/EditAnnouncementForm.jsx
// Form for editing existing announcements

import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const EditAnnouncementForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    priority: "normal",
    start_date: "",
    end_date: "",
    is_active: true,
  });
  const [errors, setErrors] = useState({});

  // Fetch existing announcement
  const fetchAnnouncement = useCallback(async () => {
    try {
      const response = await secureAxios.get(`/api/announcements/${id}`);
      const announcement = response.data;

      setFormData({
        title: announcement.title,
        content: announcement.content,
        priority: announcement.priority,
        start_date: announcement.start_date
          ? new Date(announcement.start_date).toISOString().slice(0, 16)
          : "",
        end_date: announcement.end_date
          ? new Date(announcement.end_date).toISOString().slice(0, 16)
          : "",
        is_active: announcement.is_active,
      });
    } catch (err) {
      console.error("Error fetching announcement:", err);
      toast.error("Failed to load announcement");
      navigate("/crm/announcements");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchAnnouncement();
  }, [fetchAnnouncement]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    } else if (formData.title.length > 255) {
      newErrors.title = "Title must be less than 255 characters";
    }

    if (!formData.content.trim()) {
      newErrors.content = "Content is required";
    }

    if (!formData.start_date) {
      newErrors.start_date = "Start date is required";
    }

    if (formData.end_date && formData.start_date) {
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        newErrors.end_date = "End date must be after start date";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);
    try {
      await secureAxios.put(`/api/announcements/${id}`, {
        title: formData.title.trim(),
        content: formData.content.trim(),
        priority: formData.priority,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: formData.end_date
          ? new Date(formData.end_date).toISOString()
          : null,
        is_active: formData.is_active,
      });

      toast.success("Announcement updated successfully!");
      navigate("/crm/announcements");
    } catch (err) {
      console.error("Error updating announcement:", err);
      toast.error(err.response?.data?.error || "Failed to update announcement");
    } finally {
      setSaving(false);
    }
  };

  // Priority options
  const priorities = [
    {
      value: "low",
      label: "Low",
      icon: "bi-bell",
      description: "General information",
    },
    {
      value: "normal",
      label: "Normal",
      icon: "bi-info-circle",
      description: "Standard announcement",
    },
    {
      value: "high",
      label: "High",
      icon: "bi-exclamation-circle",
      description: "Important update",
    },
    {
      value: "urgent",
      label: "Urgent",
      icon: "bi-exclamation-triangle",
      description: "Critical - requires immediate attention",
    },
  ];

  if (loading) {
    return (
      <div className="container-fluid">
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-muted">Loading announcement...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
        <div>
          <h2>
            <i className="bi bi-pencil-square me-2"></i>
            Edit Announcement
          </h2>
          <p className="text-muted mb-0">Update the announcement details</p>
        </div>
        <Link to="/crm/announcements" className="btn btn-outline-secondary">
          <i className="bi bi-arrow-left me-1"></i>
          Back to List
        </Link>
      </div>

      {/* Form */}
      <div className="card">
        <div className="card-body">
          <form onSubmit={handleSubmit} className="announcement-form">
            {/* Active Status */}
            <div className="mb-4">
              <div className="form-check form-switch">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="is_active">
                  <strong>Active</strong>
                  <span className="text-muted ms-2">
                    {formData.is_active
                      ? "Announcement is visible to users"
                      : "Announcement is hidden from users"}
                  </span>
                </label>
              </div>
            </div>

            {/* Title */}
            <div className="mb-3">
              <label htmlFor="title" className="form-label">
                Title <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                className={`form-control ${errors.title ? "is-invalid" : ""}`}
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="Enter announcement title"
                maxLength={255}
              />
              {errors.title && (
                <div className="invalid-feedback">{errors.title}</div>
              )}
              <small className="text-muted">
                {formData.title.length}/255 characters
              </small>
            </div>

            {/* Content */}
            <div className="mb-3">
              <label htmlFor="content" className="form-label">
                Content <span className="text-danger">*</span>
              </label>
              <textarea
                className={`form-control ${errors.content ? "is-invalid" : ""}`}
                id="content"
                name="content"
                value={formData.content}
                onChange={handleChange}
                placeholder="Enter announcement content. You can use line breaks for formatting."
                rows={6}
              />
              {errors.content && (
                <div className="invalid-feedback">{errors.content}</div>
              )}
              <small className="text-muted">
                Tip: Use line breaks to separate paragraphs
              </small>
            </div>

            {/* Priority */}
            <div className="mb-4">
              <label className="form-label">
                Priority <span className="text-danger">*</span>
              </label>
              <div className="priority-selector">
                {priorities.map((priority) => (
                  <div
                    key={priority.value}
                    className={`priority-option ${priority.value}`}
                  >
                    <input
                      type="radio"
                      id={`priority-${priority.value}`}
                      name="priority"
                      value={priority.value}
                      checked={formData.priority === priority.value}
                      onChange={handleChange}
                    />
                    <label htmlFor={`priority-${priority.value}`}>
                      <i className={`bi ${priority.icon} d-block mb-1`}></i>
                      <strong>{priority.label}</strong>
                      <small className="d-block text-muted">
                        {priority.description}
                      </small>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Date Range */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label htmlFor="start_date" className="form-label">
                  Start Date <span className="text-danger">*</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${
                    errors.start_date ? "is-invalid" : ""
                  }`}
                  id="start_date"
                  name="start_date"
                  value={formData.start_date}
                  onChange={handleChange}
                />
                {errors.start_date && (
                  <div className="invalid-feedback">{errors.start_date}</div>
                )}
                <small className="text-muted">
                  When the announcement becomes visible
                </small>
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="end_date" className="form-label">
                  End Date <span className="text-muted">(optional)</span>
                </label>
                <input
                  type="datetime-local"
                  className={`form-control ${
                    errors.end_date ? "is-invalid" : ""
                  }`}
                  id="end_date"
                  name="end_date"
                  value={formData.end_date}
                  onChange={handleChange}
                  min={formData.start_date}
                />
                {errors.end_date && (
                  <div className="invalid-feedback">{errors.end_date}</div>
                )}
                <small className="text-muted">
                  Leave empty for no expiration
                </small>
              </div>
            </div>

            {/* Preview */}
            {(formData.title || formData.content) && (
              <div className="mb-4">
                <label className="form-label">Preview</label>
                <div
                  className={`card ${!formData.is_active ? "opacity-50" : ""}`}
                  style={{
                    borderLeft: `4px solid ${
                      formData.priority === "urgent"
                        ? "#dc3545"
                        : formData.priority === "high"
                        ? "#ffc107"
                        : formData.priority === "normal"
                        ? "#0dcaf0"
                        : "#6c757d"
                    }`,
                  }}
                >
                  <div className="card-header">
                    <h5
                      className="mb-0"
                      style={{
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {formData.title || "Untitled"}
                      {!formData.is_active && (
                        <span className="badge bg-secondary ms-2">
                          Inactive
                        </span>
                      )}
                    </h5>
                  </div>
                  <div className="card-body">
                    <div
                      style={{
                        whiteSpace: "pre-wrap",
                        wordWrap: "break-word",
                        overflowWrap: "break-word",
                      }}
                    >
                      {formData.content || "No content"}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submit Buttons */}
            <div className="d-flex justify-content-end gap-2">
              <Link to="/crm/announcements" className="btn btn-secondary">
                Cancel
              </Link>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-1"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bi bi-check-lg me-1"></i>
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditAnnouncementForm;
